import { Box, Typography, TextField, Button } from "@mui/material"
import { useState } from "react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { AccountsApi, ResponseError } from "../../clients"
import { getConfiguration } from "../../clients/configuration"
import { addAlertWithTimeout } from "../../redux/features/alerts/alertsHelpers"
import { setCurrentUser } from "../../redux/features/currentUser/currentUserSlice"
import { AppAlert } from "../AppAlerts/AppAlerts"

export const MyAccountInformation = () => {

    const user = useAppSelector(state => state.currentUser.account)
    const usingGoogle = useAppSelector(state => state.auth.accessTokenPayload.username.toLowerCase().includes('google'))
    const dispatch = useAppDispatch()

    const [username, setUsername] = useState<string>(user?.username || '')
    const [email, setEmail] = useState<string>(user?.email || '')
    const [emailError, setEmailError] = useState<string>('')
    const [first, setFirst] = useState<string>(user?.givenName || '')
    const [last, setLast] = useState<string>(user?.familyName || '')
    const [phoneNumber, setPhoneNumber] = useState<string>(user?.phoneNumber || '')
    const [saving, setSaving] = useState<boolean>(false)

    const hasChanges = user && (username !== user.username || first !== user.givenName || last !== user.familyName || phoneNumber !== user.phoneNumber)
    const hasError = !!emailError

    const handlePhoneNumberChange = (event) => {
        let input = event.target.value.replace(/\D/g, ''); // Remove all non-digit characters

        if (input.length > 3 && input.length <= 6) {
            input = input.replace(/(\d{3})(\d+)/, '$1-$2');
        } else if (input.length > 6) {
            input = input.replace(/(\d{3})(\d{3})(\d+)/, '$1-$2-$3');
        }

        setPhoneNumber(input);
    };

    const handleEmailBlur = (): string => {
        const emailRegex = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g)
        if (!email.match(emailRegex)?.length) {
            const error = 'Invalid email address'
            setEmailError(error)
            return error
        }
        return ''
    }

    const handleSaveChanges = async () => {
        const config = await getConfiguration()
        const accountsApi = new AccountsApi(config)
        setSaving(true)
        accountsApi.updateAccount({
            accountID: user.id,
            updateAccountRequest: {
                username: username,
                givenName: first,
                familyName: last,
                phoneNumber: phoneNumber
            }
        }).then((res) => {
            setSaving(false)
            const appAlert = new AppAlert('success', 'Account updated!')
            addAlertWithTimeout(dispatch, appAlert)
            dispatch(setCurrentUser({
                ...res.data,
                createdDate: new Date(res.data.createdDate).toISOString(),
                updatedDate: new Date(res.data.updatedDate).toISOString(),
                lastSeenDate: new Date(res.data.lastSeenDate).toISOString(),
            }))
        }).catch(async (err) => {
            setSaving(false)
            const responseErr = err as ResponseError
            if (responseErr.response.status === 409) {
                const text = await responseErr.response.text().catch(() => '')
                const appAlert = new AppAlert('error', text)
                addAlertWithTimeout(dispatch, appAlert, 3000)
            }
            const appAlert = new AppAlert('error', 'Something went wrong. Try again later.')
            addAlertWithTimeout(dispatch, appAlert, 3000)
        })
    }

    return (
        <Box className="my-account__content-main" sx={{ flex: 1 }}>
            <Box>
                <Typography variant="h4" sx={{ fontSize: '1.2rem', fontWeight: 400 }}>Username</Typography>
                <TextField fullWidth value={username} sx={{ mt: 1 }} onChange={e => setUsername(e.target.value)} helperText={`${username.length}/45`} />
            </Box>
            <Box sx={{ mt: 3 }}>
                <Typography variant="h4" sx={{ fontSize: '1.2rem', fontWeight: 400 }}>First Name</Typography>
                <TextField fullWidth value={first} sx={{ mt: 1 }} onChange={e => setFirst(e.target.value)} />
            </Box>
            <Box sx={{ mt: 3 }}>
                <Typography variant="h4" sx={{ fontSize: '1.2rem', fontWeight: 400 }}>Last Name</Typography>
                <TextField fullWidth value={last} sx={{ mt: 1 }} onChange={e => setLast(e.target.value)} />
            </Box>
            <Box sx={{ mt: 3 }}>
                <Typography variant="h4" sx={{ fontSize: '1.2rem', fontWeight: 400 }}>Email</Typography>
                <TextField
                    fullWidth
                    value={email}
                    disabled={usingGoogle}
                    sx={{ mt: 1 }}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    error={!!emailError}
                    helperText={emailError}
                />
            </Box>
            <Box sx={{ mt: 3 }}>
                <Typography variant="h4" sx={{ fontSize: '1.2rem', fontWeight: 400 }}>Phone Number</Typography>
                <TextField fullWidth value={phoneNumber} sx={{ mt: 1 }} onChange={handlePhoneNumberChange} />
            </Box>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button variant="contained" disabled={saving || !hasChanges || hasError} onClick={handleSaveChanges}>Save Changes</Button>
            </Box>
        </Box>
    )
}